import { useLazyQuery } from '@apollo/client'
import { Info, Warning } from '@mui/icons-material'
import { Box, Dialog, DialogActions, DialogContent, Grid, Tooltip, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { OutlinedCross } from '../../../assets/icons'
import { COLLISION_DETECTION_QUERY } from '../../../graphql/queries'
import { Button } from '../../atoms'

interface CollisionDetectionProps {
  disabled?: boolean
}

const CollisionDetection: React.FC<CollisionDetectionProps> = ({ disabled }) => {
  const { t } = useTranslation(['collisionDetection', 'common'])
  const [open, setOpen] = useState(false)
  const { watch, setValue } = useFormContext()

  const firstname = watch('conflictPartnerFirstname')
  const lastname = watch('conflictPartnerLastname')
  const postalCode = watch('conflictPartnerPostalCode')
  const needsCollisionDetection = watch('needsCollisionDetection')

  const [startCollisionDetection, { called, loading, data }] = useLazyQuery(
    COLLISION_DETECTION_QUERY,
    {
      fetchPolicy: 'network-only'
    }
  )

  const onButtonClick = () => {
    startCollisionDetection({
      variables: {
        firstname,
        lastname,
        postalCode
      }
    })
  }

  const onCollisionDetectedButtonClick = () => {
    setValue('collisionConfirmed', true, { shouldDirty: true })
    setValue('completeReason', 'service_call', { shouldDirty: true })
    // closingResult nicht mehr setzen - Backend (IncomingCallAction#set_collision_state)
    // verwendet das Result-System und setzt das korrekte Result automatisch
    setOpen(false)
    const submitButton = document.getElementById('editor-modal-save-button')
    submitButton?.click()
  }

  const onNoCollisionButtonClick = () => {
    setValue('noCollisionByHuman', true, { shouldDirty: true })
    setOpen(false)
  }

  const noCollisionByHuman = watch('noCollisionByHuman')

  useEffect(() => {
    if (called && !loading && data && (data?.m2CasesByCollisionDetection?.length || 0) > 0) {
      setOpen(true)
    } else {
      setOpen(false)
    }
  }, [data, loading, called])

  if (disabled) {
    if (noCollisionByHuman) {
      return (
        <Grid sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ width: 'max-content' }}>
            <em>{t('collisionDetection:markedAsNoCollision')}</em>
          </Typography>
        </Grid>
      )
    }
    return null
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            id="fakeSubmitButton"
            color="primary"
            onClick={onButtonClick}
            fullWidth
            disabled={!needsCollisionDetection || !firstname || !lastname || !postalCode}
          >
            {t('startCollisionDetection')}
          </Button>
        </Grid>
        <Grid sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={t('collisionDetection:hint')} sx={{ mx: 2 }}>
            <Info />
          </Tooltip>
        </Grid>
        {noCollisionByHuman ? (
          <Grid sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ width: 'max-content' }}>
              <em>{t('collisionDetection:markedAsNoCollision')}</em>
            </Typography>
          </Grid>
        ) : null}
      </Grid>
      <Dialog open={open} sx={{ maxWidth: 'lg' }} data-testid="sms-modal">
        <DialogContent>
          <>
            <Typography variant="h5">{t('collisionDetection:title')}</Typography>
            <Typography variant="caption" sx={{ pt: 1 }}>
              {t('collisionDetection:subtitle')}
            </Typography>
            {(data?.m2CasesByCollisionDetection?.length || 0) > 0 ? (
              <Grid container sx={{ mt: 4 }} spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="body2">
                    <strong>{t('collisionDetection:claimDate')}</strong>
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="body2">
                    <strong>{t('collisionDetection:comments')}</strong>
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="body2">
                    <strong>{t('collisionDetection:legalArea')}</strong>
                  </Typography>
                </Grid>
              </Grid>
            ) : null}
            {data?.m2CasesByCollisionDetection?.map((m2Case: any, index: number) => (
              <Grid container spacing={2} key={m2Case.id || index}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="body2">{m2Case.claimDate}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="body2">{m2Case.comments}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="body2">{m2Case.m2LegalArea?.title}</Typography>
                </Grid>
              </Grid>
            ))}
          </>
        </DialogContent>
        <DialogActions>
          <Box>
            <Button
              color="secondary"
              sx={{ marginRight: '15px' }}
              onClick={onNoCollisionButtonClick}
              startIcon={<OutlinedCross />}
            >
              {t('collisionDetection:noCollision')}
            </Button>
            <Button
              color="primary"
              sx={{ marginRight: '15px' }}
              onClick={onCollisionDetectedButtonClick}
              startIcon={<Warning />}
            >
              {t('collisionDetection:collisionDetected')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default CollisionDetection
